import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useCallback } from "react";

import {
  getGetJobByIdQueryKey,
  getListHealthConcernsQueryKey,
} from "@elation/api-client/src/stitch-web";

import { useCurrentPatient } from "@src/features/patients";
import { Button } from "@src/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/shared/components/ui/popover";
import { events } from "@src/shared/utils/observability/events";

import { ImportPanel } from "./ImportPanel";
import { useDocumentJobs } from "./useDocumentJobs";

interface DocumentUploadProps {
  onNewOpportunities: (ids: number[]) => void;
}

interface CompletedJobMetadata {
  opportunityIds?: number[];
}

export const DocumentUploadInner = ({
  patientId,
  onNewOpportunities,
}: {
  patientId: number;
  onNewOpportunities: (ids: number[]) => void;
}) => {
  const queryClient = useQueryClient();
  const { jobs, addJob, markJobFailed, removeJob } = useDocumentJobs(patientId);

  const handleJobComplete = useCallback(
    (jobId: string) => {
      queryClient.invalidateQueries({
        queryKey: getListHealthConcernsQueryKey(patientId),
      });

      const jobData = queryClient.getQueryData(getGetJobByIdQueryKey(jobId)) as
        | { data?: { metadata?: CompletedJobMetadata } }
        | undefined;
      const newIds = jobData?.data?.metadata?.opportunityIds ?? [];
      // Fire the funnel's "extracted" leg for every completed upload — a count
      // of 0 is signal too (document processed, no care gaps found).
      //
      // Known limitation: jobs are persisted in localStorage and resume polling
      // after a page reload, so a job that completes in a later session emits
      // `care gaps extracted` with no matching `document uploaded` in that
      // session. This skews session-scoped funnel math. The durable fix is to
      // emit this event server-side when the job finishes; tracked as a
      // follow-up. See the PR description's Consequences section.
      events.careGapsExtracted.track({
        patient_id: patientId,
        count: newIds.length,
      });
      if (newIds.length > 0) {
        onNewOpportunities(newIds);
      }
    },
    [patientId, queryClient, onNewOpportunities],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline">
          <Plus />
          Import Document
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px]">
        <ImportPanel
          patientId={patientId}
          jobs={jobs}
          onJobAdded={addJob}
          onJobFailed={markJobFailed}
          onJobComplete={handleJobComplete}
          onJobRemove={removeJob}
        />
      </PopoverContent>
    </Popover>
  );
};

export const DocumentUpload = ({ onNewOpportunities }: DocumentUploadProps) => {
  const currentPatient = useCurrentPatient();

  if (!currentPatient?.id) return null;

  return (
    <DocumentUploadInner
      patientId={currentPatient.id}
      onNewOpportunities={onNewOpportunities}
    />
  );
};
